import React from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, Star, Zap } from 'lucide-react'

/**
 * XPProgressBar Component
 * Gamified visual XP progress display
 */

const XPProgressBar = ({ currentXP = 0, currentLevel = 1, className = '' }) => {
  // XP required for each level (exponential growth)
  const getXPForLevel = (level) => {
    return level * 100 + (level - 1) * 50
  }

  const currentLevelXP = currentLevel > 1 ? getXPForLevel(currentLevel - 1) : 0
  const nextLevelXP = getXPForLevel(currentLevel)
  const xpInCurrentLevel = currentXP - currentLevelXP
  const xpNeededForNextLevel = nextLevelXP - currentLevelXP
  const progressPercentage = (xpInCurrentLevel / xpNeededForNextLevel) * 100

  const getLevelTitle = (level) => {
    const titles = {
      1: 'Survivor Rookie',
      2: 'Prepared Citizen',
      3: 'Crisis Navigator',
      4: 'Survival Expert',
      5: 'Doomsday Veteran',
      6: 'Apocalypse Master',
      7: 'Legend of the Wastes'
    }
    return titles[Math.min(level, 7)] || 'Ultimate Survivor'
  }

  return (
    <motion.div 
      className={`xp-progress-container ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Level Badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            whileHover={{ scale: 1.05 }}
            className="level-badge"
            style={{
              background: 'linear-gradient(135deg, var(--secondary) 0%, var(--danger) 100%)',
              borderRadius: '50%',
              padding: '16px',
              boxShadow: '0 8px 24px hsla(25, 95%, 53%, 0.3)'
            }}
          >
            <span className="text-white font-bold text-lg tracking-tight">LV {currentLevel}</span>
          </motion.div>
          <div>
            <h3 className="font-bold text-white">{getLevelTitle(currentLevel)}</h3>
            <p className="text-sm text-gray-400">
              {currentXP.toLocaleString()} XP Total
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-sm text-gray-400">Next Level</p>
          <p className="font-bold text-orange-400">
            {xpInCurrentLevel}/{xpNeededForNextLevel} XP
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative">
        <div 
          className="bg-gray-800 rounded-full h-8 overflow-hidden"
          style={{
            background: 'var(--neutral-800)',
            border: '1px solid var(--glass-border)'
          }}
        >
          <motion.div
            className="h-full relative"
            style={{
              background: 'linear-gradient(90deg, var(--secondary) 0%, var(--danger) 100%)',
              boxShadow: '0 0 20px hsla(25, 95%, 53%, 0.5)'
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            {/* Animated shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              animate={{ x: [-200, 200] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            />
          </motion.div>
        </div>
        
        {/* Progress Percentage */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <span className="text-sm font-bold text-white drop-shadow-lg tracking-wide">
            {Math.round(progressPercentage)}%
          </span>
        </motion.div>
      </div>

      {/* Milestone Indicators */}
      <div className="flex justify-between mt-2">
        {[0, 25, 50, 75, 100].map((milestone) => (
          <div
            key={milestone}
            className={`flex flex-col items-center ${
              progressPercentage >= milestone ? 'text-orange-400' : 'text-gray-600'
            }`}
          >
            {milestone === 100 ? (
              <Star className="w-4 h-4" />
            ) : milestone === 50 ? (
              <Zap className="w-4 h-4" />
            ) : (
              <div className="w-2 h-2 rounded-full bg-current" />
            )}
          </div>
        ))}
      </div>

      {/* Bonus XP Events */}
      {progressPercentage > 90 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 bg-yellow-900/20 border border-yellow-800 rounded-lg p-2 text-center"
        >
          <p className="text-xs text-yellow-400 flex items-center justify-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Level up imminent! Push for that final stretch!
          </p>
        </motion.div>
      )}
    </motion.div>
  )
}

export default XPProgressBar